import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Loader2, Search, Play, Download, RefreshCw, Check } from 'lucide-react';

/**
 * Common Leisure · Video Stüdyosu — ürün görsellerinden Instagram videosu.
 * Route: /common-leisure/video (sidebar VAR, panelin kendi dilinde)
 *
 * Kurgu markanın kendi reels'lerinden alındı: aynı modelin farklı açıdan
 * çekilmiş kareleri sert kesişle değişir, alt şeritteki iki metin bloğu sabit.
 * Üretim glowytics-backend/scripts/cl_video/render.py içinde; burası seçim
 * ekranı.
 *
 * 🔑 Marka platformda kayıtlı DEĞİL: katalog doğrudan tr.commonleisureweb.com
 *    üzerinden okunuyor, marka seçimine bağlı bir veri yok.
 * 🔑 Video api.glowytics.com/pixel/cl-video/ altından servis ediliyor; <video>
 *    ve indirme bağlantısı MUTLAK adres ister, panelin kendi origin'i değil.
 */

/* 🔴 services/apiProxy varsayılan dışa aktarımı bir axios örneği DEĞİL, kendi
   `request(...)` sarmalayıcısı; `.get` yok. Panelin geri kalanı gibi düz fetch
   kullanılıyor. */
const API_KOK = (() => {
  const ham = (process.env.REACT_APP_API_URL || 'https://api.glowytics.com').trim();
  return ham.replace(/\/+$/, '').replace(/\/api$/, '');
})();
// 🔑 Video /pixel altından servis ediliyor, /api altından DEĞİL.
const medya = (yol) => (yol && yol.startsWith('/') ? API_KOK + yol : yol);

async function iste(yol, secenek = {}) {
  const r = await fetch(`${API_KOK}/api${yol}`, {
    credentials: 'include',
    headers: { 'Content-Type': 'application/json', 'X-Brand-ID': localStorage.getItem('currentBrandId') || '' },
    ...secenek,
  });
  let j = null;
  try { j = await r.json(); } catch (e) { /* gövdesiz hata */ }
  if (!r.ok) throw new Error((j && (j.detay || j.error)) || `İstek başarısız (${r.status})`);
  return j;
}

const T = {
  zemin: '#F6F8FF', kart: '#FFFFFF', cizgi: '#E5E7EB', cizgi2: '#F3F4F6',
  metin: '#111827', metin2: '#374151', metin3: '#6B7280', metin4: '#9CA3AF',
  mavi: '#436AEB', maviYumusak: '#EEF2FE',
};

export default function CommonLeisureVideo() {
  const [urunler, setUrunler] = useState([]);
  const [yukleniyor, setYukleniyor] = useState(true);
  const [arama, setArama] = useState('');
  const [secili, setSecili] = useState(null);      // { handle, baslik, ... }
  const [detay, setDetay] = useState(null);         // { gorseller, sol, sag }
  const [detayYuk, setDetayYuk] = useState(false);
  const [sol, setSol] = useState('');
  const [sag, setSag] = useState('');
  const [sure, setSure] = useState(7);
  const [kareSecim, setKareSecim] = useState([0, 1, 2]);
  const [uretiliyor, setUretiliyor] = useState(false);
  const [sonuc, setSonuc] = useState(null);
  const [hata, setHata] = useState('');
  const videoRef = useRef(null);

  useEffect(() => {
    let alive = true;
    iste('/cl-video/products')
      .then((d) => { if (alive) setUrunler(d.urunler || []); })
      .catch((e) => { if (alive) setHata(e.message || 'Katalog alınamadı'); })
      .finally(() => { if (alive) setYukleniyor(false); });
    return () => { alive = false; };
  }, []);

  const listelenen = useMemo(() => {
    const q = arama.toLowerCase().trim();
    if (!q) return urunler.slice(0, 60);
    return urunler.filter((p) => p.baslik.toLowerCase().includes(q) || p.handle.includes(q)).slice(0, 60);
  }, [urunler, arama]);

  const urunSec = useCallback((p) => {
    setSecili(p); setDetay(null); setSonuc(null); setHata(''); setDetayYuk(true);
    iste(`/cl-video/product/${p.handle}`)
      .then((d) => {
        setDetay(d);
        setSol((d.sol || []).join('\n'));
        setSag((d.sag || []).join('\n'));
        // Varsayılan: ilk üç kare. Ürünün iki karesi varsa ikisi.
        setKareSecim((d.gorseller || []).slice(0, 3).map((_, i) => i));
      })
      .catch((e) => setHata(e.message || 'Ürün okunamadı'))
      .finally(() => setDetayYuk(false));
  }, []);

  const kareTikla = (i) => setKareSecim((s) => {
    if (s.includes(i)) return s.length > 1 ? s.filter((x) => x !== i) : s;
    return s.length >= 3 ? s : [...s, i].sort((a, b) => a - b);
  });

  const uret = () => {
    if (!secili) return;
    setUretiliyor(true); setHata(''); setSonuc(null);
    iste('/cl-video/generate', {
      method: 'POST',
      body: JSON.stringify({
        handle: secili.handle,
        sol: sol.split('\n').map((s) => s.trim()).filter(Boolean),
        sag: sag.split('\n').map((s) => s.trim()).filter(Boolean),
        sure: Number(sure) || 7,
        secili: kareSecim,
      }),
    })
      .then((d) => {
        setSonuc(d);
        // Aynı handle'a tekrar üretilince dosya adı değişmiyor: tarayıcı eski
        // videoyu önbellekten verir, "değişmedi" sanılır. Zaman damgası şart.
        setTimeout(() => { if (videoRef.current) videoRef.current.load(); }, 50);
      })
      .catch((e) => setHata(e.message || 'Video üretilemedi'))
      .finally(() => setUretiliyor(false));
  };

  const kutu = { background: T.kart, border: `1px solid ${T.cizgi}`, borderRadius: 12, boxShadow: '0 1px 2px rgba(16,24,40,.04)' };
  const etiket = { fontSize: 12, fontWeight: 600, color: T.metin3, marginBottom: 6, display: 'block' };
  const girdi = {
    width: '100%', border: `1px solid ${T.cizgi}`, borderRadius: 8, padding: '8px 10px',
    fontSize: 13, color: T.metin, fontFamily: 'inherit', outline: 'none', resize: 'vertical',
  };

  return (
    <div style={{ background: T.zemin, minHeight: '100%', padding: 24, fontVariantNumeric: 'tabular-nums' }}>
      <div style={{ maxWidth: 1360, margin: '0 auto' }}>
        <div style={{ marginBottom: 18 }}>
          <h1 style={{ fontSize: 22, fontWeight: 700, color: T.metin, margin: 0 }}>Common Leisure · Video Stüdyosu</h1>
          <p style={{ fontSize: 13.5, color: T.metin3, margin: '6px 0 0' }}>
            Ürünü seç, üç kare sert kesişle değişsin. Alt şerit markanın kendi reels düzeninde:
            solda giysi ve kumaş, sağda koleksiyon ve alan adı. Çıktı 1080×1350, Instagram için hazır.
          </p>
        </div>

        {hata ? (
          <div style={{ ...kutu, borderColor: '#FECACA', background: '#FEF2F2', color: '#991B1B', padding: '10px 14px', fontSize: 13, marginBottom: 14 }}>
            {hata}
          </div>
        ) : null}

        <div style={{ display: 'grid', gridTemplateColumns: '320px 1fr 380px', gap: 16, alignItems: 'start' }}>

          {/* ---- ürün listesi ---- */}
          <div style={{ ...kutu, padding: 14, maxHeight: 'calc(100vh - 190px)', display: 'flex', flexDirection: 'column' }}>
            <div style={{ position: 'relative', marginBottom: 10 }}>
              <Search size={14} style={{ position: 'absolute', left: 10, top: 10, color: T.metin4 }} />
              <input
                value={arama}
                onChange={(e) => setArama(e.target.value)}
                placeholder="Ürün ara"
                style={{ ...girdi, paddingLeft: 30 }}
              />
            </div>
            <div style={{ fontSize: 11.5, color: T.metin4, marginBottom: 8 }}>
              {yukleniyor ? 'Katalog yükleniyor' : `${urunler.length} ürün, en yenisi üstte`}
            </div>
            <div style={{ overflowY: 'auto', margin: '0 -6px', paddingRight: 2 }}>
              {yukleniyor ? (
                <div style={{ display: 'flex', justifyContent: 'center', padding: 30 }}><Loader2 size={20} className="animate-spin" color={T.mavi} /></div>
              ) : listelenen.map((p) => {
                const on = secili && secili.handle === p.handle;
                return (
                  <button
                    key={p.handle}
                    type="button"
                    onClick={() => urunSec(p)}
                    style={{
                      display: 'flex', gap: 10, alignItems: 'center', width: '100%', textAlign: 'left',
                      padding: '7px 6px', border: 0, borderRadius: 9, cursor: 'pointer',
                      background: on ? T.maviYumusak : 'transparent', fontFamily: 'inherit',
                    }}
                  >
                    <img
                      src={p.kapak}
                      alt=""
                      style={{ width: 34, height: 44, objectFit: 'cover', borderRadius: 5, background: T.cizgi2, flex: '0 0 auto' }}
                    />
                    <span style={{ minWidth: 0 }}>
                      <span style={{ display: 'block', fontSize: 12.5, color: on ? T.mavi : T.metin2, fontWeight: on ? 600 : 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {p.baslik}
                      </span>
                      <span style={{ display: 'block', fontSize: 11, color: T.metin4 }}>
                        {p.gorsel} görsel{p.stokta ? '' : ' · stokta yok'}
                      </span>
                    </span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* ---- kareler ---- */}
          <div style={{ ...kutu, padding: 16, minHeight: 320 }}>
            {!secili ? (
              <div style={{ color: T.metin4, fontSize: 13.5, padding: '90px 0', textAlign: 'center' }}>
                Soldan bir ürün seç.
              </div>
            ) : detayYuk ? (
              <div style={{ display: 'flex', justifyContent: 'center', padding: 90 }}><Loader2 size={22} className="animate-spin" color={T.mavi} /></div>
            ) : detay ? (
              <>
                <div style={{ fontSize: 14.5, fontWeight: 600, color: T.metin, marginBottom: 2 }}>{detay.baslik}</div>
                <div style={{ fontSize: 12, color: T.metin4, marginBottom: 14 }}>
                  Videoya girecek kareleri seç, sıra soldan sağa. En fazla üç kare.
                </div>
                <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                  {(detay.gorseller || []).map((g, i) => {
                    const sira = kareSecim.indexOf(i);
                    return (
                      <button
                        key={g}
                        type="button"
                        onClick={() => kareTikla(i)}
                        style={{
                          position: 'relative', padding: 0, border: `2px solid ${sira >= 0 ? T.mavi : T.cizgi}`,
                          borderRadius: 10, overflow: 'hidden', cursor: 'pointer', background: T.cizgi2, lineHeight: 0,
                        }}
                      >
                        <img src={g} alt="" style={{ width: 132, height: 165, objectFit: 'cover', opacity: sira >= 0 ? 1 : 0.5 }} />
                        {sira >= 0 ? (
                          <span style={{
                            position: 'absolute', top: 6, left: 6, width: 20, height: 20, borderRadius: '50%',
                            background: T.mavi, color: '#fff', fontSize: 11.5, fontWeight: 700,
                            display: 'flex', alignItems: 'center', justifyContent: 'center', lineHeight: 1,
                          }}>{sira + 1}</span>
                        ) : null}
                      </button>
                    );
                  })}
                </div>
              </>
            ) : null}
          </div>

          {/* ---- ayarlar ve çıktı ---- */}
          <div style={{ ...kutu, padding: 16 }}>
            <label style={etiket}>Sol alt · giysi ve kumaş</label>
            <textarea rows={2} value={sol} onChange={(e) => setSol(e.target.value)} style={{ ...girdi, marginBottom: 12 }} placeholder="Her satır ayrı bir giysi" />

            <label style={etiket}>Sağ alt · koleksiyon ve alan adı</label>
            <textarea rows={2} value={sag} onChange={(e) => setSag(e.target.value)} style={{ ...girdi, marginBottom: 12 }} />

            <label style={etiket}>Süre</label>
            <div style={{ display: 'flex', gap: 6, marginBottom: 16 }}>
              {[5, 7, 9, 12].map((s) => (
                <button
                  key={s}
                  type="button"
                  onClick={() => setSure(s)}
                  style={{
                    flex: 1, padding: '7px 0', fontSize: 12.5, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit',
                    border: `1px solid ${sure === s ? T.mavi : T.cizgi}`, borderRadius: 8,
                    background: sure === s ? T.maviYumusak : '#fff', color: sure === s ? T.mavi : T.metin2,
                  }}
                >{s} sn</button>
              ))}
            </div>

            <button
              type="button"
              onClick={uret}
              disabled={!secili || uretiliyor || !kareSecim.length}
              style={{
                width: '100%', padding: '10px 0', border: 0, borderRadius: 9, cursor: (!secili || uretiliyor) ? 'default' : 'pointer',
                background: (!secili || uretiliyor) ? '#C7D2FE' : T.mavi, color: '#fff', fontSize: 13.5, fontWeight: 600,
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, fontFamily: 'inherit',
              }}
            >
              {uretiliyor ? <><Loader2 size={15} className="animate-spin" /> Üretiliyor</> : <><Play size={15} /> Videoyu üret</>}
            </button>

            {sonuc ? (
              <div style={{ marginTop: 16, paddingTop: 16, borderTop: `1px solid ${T.cizgi2}` }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12.5, color: '#047857', fontWeight: 600, marginBottom: 10 }}>
                  <Check size={14} /> {sonuc.gorsel} kare · {sonuc.sure} sn · {(sonuc.boyut / 1048576).toFixed(1)} MB
                </div>
                <video
                  ref={videoRef}
                  key={sonuc.url}
                  src={`${medya(sonuc.url)}?v=${Date.now()}`}
                  poster={medya(sonuc.kapakUrl)}
                  controls
                  autoPlay
                  loop
                  muted
                  playsInline
                  style={{ width: '100%', borderRadius: 10, background: '#000', display: 'block' }}
                />
                <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
                  <a
                    href={medya(sonuc.url)}
                    download={`${sonuc.handle}.mp4`}
                    style={{
                      flex: 1, padding: '8px 0', borderRadius: 8, border: `1px solid ${T.cizgi}`, background: '#fff',
                      color: T.metin2, fontSize: 12.5, fontWeight: 600, textAlign: 'center', textDecoration: 'none',
                      display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                    }}
                  >
                    <Download size={14} /> İndir
                  </a>
                  <button
                    type="button"
                    onClick={uret}
                    style={{
                      padding: '8px 12px', borderRadius: 8, border: `1px solid ${T.cizgi}`, background: '#fff',
                      color: T.metin2, fontSize: 12.5, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit',
                      display: 'flex', alignItems: 'center', gap: 6,
                    }}
                  >
                    <RefreshCw size={14} /> Yeniden
                  </button>
                </div>
              </div>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
}
